import { Box, SimpleGrid, Heading, Text, Flex, Button } from '@chakra-ui/react'
import { AlertTriangle, Calendar, ArrowRight } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import { AccountingStats, FiscalYearSelector, OwnerAccountsTable } from '../../components'
import { useBudgets, useFundCalls, useUnpaidOwners } from '../../hooks'
import { useAccountingContext } from '../../context/AccountingContext'

export function AccountingDashboardTab() {
  const navigate = useNavigate()
  const { residenceId, activeFiscalYear, setSelectedFiscalYear } = useAccountingContext()
  const fiscalYearId = activeFiscalYear?.id
  const { data: budgets } = useBudgets(residenceId, fiscalYearId)
  const { data: fundCalls } = useFundCalls(residenceId, fiscalYearId)
  const { data: unpaidOwners } = useUnpaidOwners(residenceId)

  const activeBudget = budgets?.find((b) => b.status === 'active' || b.status === 'voted')
  const nextFundCall = fundCalls?.find((fc) => fc.status === 'draft' || fc.status === 'sent')
  const overdueCount = fundCalls?.filter((fc) => fc.status === 'overdue').length || 0

  return (
    <Box>
      {/* Header avec sélecteur d'exercice */}
      <Flex justify="flex-end" mb={4}>
        <FiscalYearSelector
          residenceId={residenceId}
          value={activeFiscalYear?.id}
          onChange={setSelectedFiscalYear}
        />
      </Flex>

      {/* Stats globales */}
      <AccountingStats residenceId={residenceId} />

      <SimpleGrid columns={{ base: 1, lg: 2 }} gap={6} mt={6}>
        {/* Budget actif */}
        <Box bg="white" p={5} borderRadius="lg" boxShadow="sm" border="1px solid" borderColor="gray.100">
          <Flex justify="space-between" align="center" mb={4}>
            <Heading size="md">Budget en cours</Heading>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate(`/app/accounting/${residenceId}/budgets`)}
            >
              Voir tout
              <ArrowRight size={16} />
            </Button>
          </Flex>

          {activeBudget ? (
            <Box>
              <Text fontWeight="semibold" fontSize="lg" mb={2}>
                {activeBudget.name}
              </Text>
              <Flex gap={4}>
                <Box>
                  <Text fontSize="sm" color="gray.500">
                    Montant total
                  </Text>
                  <Text fontWeight="bold" fontSize="xl" color="brand.600">
                    {new Intl.NumberFormat('fr-FR', {
                      style: 'currency',
                      currency: 'EUR',
                    }).format(activeBudget.total_amount ?? 0)}
                  </Text>
                </Box>
                <Box>
                  <Text fontSize="sm" color="gray.500">
                    Statut
                  </Text>
                  <Text fontWeight="medium" color={activeBudget.status === 'voted' ? 'green.600' : 'blue.600'}>
                    {activeBudget.status === 'voted' ? 'Voté en AG' : 'Actif'}
                  </Text>
                </Box>
              </Flex>
            </Box>
          ) : (
            <Box textAlign="center" py={4} color="gray.500">
              <Calendar size={32} style={{ margin: '0 auto 8px' }} />
              <Text>Aucun budget actif</Text>
              <Button
                size="sm"
                colorPalette="brand"
                mt={3}
                onClick={() => navigate(`/app/accounting/${residenceId}/budgets/new`)}
              >
                Créer un budget
              </Button>
            </Box>
          )}
        </Box>

        {/* Prochain appel de fonds */}
        <Box bg="white" p={5} borderRadius="lg" boxShadow="sm" border="1px solid" borderColor="gray.100">
          <Flex justify="space-between" align="center" mb={4}>
            <Heading size="md">Appels de fonds</Heading>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate(`/app/accounting/${residenceId}/budgets`)}
            >
              Voir tout
              <ArrowRight size={16} />
            </Button>
          </Flex>

          {overdueCount > 0 && (
            <Box
              mb={4}
              p={3}
              bg="red.50"
              borderRadius="md"
              borderLeft="3px solid"
              borderColor="red.400"
            >
              <Flex align="center" gap={2}>
                <AlertTriangle size={16} color="#E53E3E" />
                <Text fontSize="sm" color="red.700" fontWeight="medium">
                  {overdueCount} appel(s) en retard de paiement
                </Text>
              </Flex>
            </Box>
          )}

          {nextFundCall ? (
            <Box>
              <Text fontWeight="semibold" mb={1}>
                {nextFundCall.label}
              </Text>
              <Text fontSize="sm" color="gray.500" mb={2}>
                Échéance: {new Date(nextFundCall.due_date).toLocaleDateString('fr-FR')}
              </Text>
              <Text fontWeight="bold" fontSize="lg" color="brand.600">
                {new Intl.NumberFormat('fr-FR', {
                  style: 'currency',
                  currency: 'EUR',
                }).format(nextFundCall.total_amount)}
              </Text>
            </Box>
          ) : (
            <Box textAlign="center" py={4} color="gray.500">
              <Text>Aucun appel en cours</Text>
            </Box>
          )}
        </Box>
      </SimpleGrid>

      {/* Impayés */}
      <Box mt={6} bg="white" p={5} borderRadius="lg" boxShadow="sm" border="1px solid" borderColor="gray.100">
        <Flex justify="space-between" align="center" mb={4}>
          <Flex align="center" gap={2}>
            <Heading size="md">Copropriétaires en impayé</Heading>
            {(unpaidOwners?.length || 0) > 0 && (
              <Box
                px={2}
                py={0.5}
                bg="red.100"
                color="red.700"
                borderRadius="full"
                fontSize="sm"
                fontWeight="bold"
              >
                {unpaidOwners?.length}
              </Box>
            )}
          </Flex>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate(`/app/accounting/${residenceId}/owners`)}
          >
            Voir tout
            <ArrowRight size={16} />
          </Button>
        </Flex>

        <OwnerAccountsTable residenceId={residenceId} showOnlyUnpaid />
      </Box>
    </Box>
  )
}
